// USUARIOS Y ROLES — port a Let's Move de la página de la agenda dental (2026-07-01).
// Módulo core `users_admin`: invitar (correo brandeado), activar/desactivar, cambiar rol,
// reenviar invitación/reset y PERMISOS POR MÓDULO (overrides personales; solo el dueño).
// Managers ven y operan (invitar staff); el dueño gestiona dueños y permisos.
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { UserPlus, Mail, KeyRound, Shield, X, Check, Loader2, Users, SlidersHorizontal, Eye, UserRound } from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import { readCache, writeCache } from '../../services/localCache';
import { useAuth } from '../../context/AuthContext';
import {
  listOperators, inviteOperator, patchOperator, resendInvite, sendReset,
  getPermissions, savePermissions, impersonate, listProfiles, applyProfile,
  Operator, UserRole, ModulePermission, PermLevel, StaffProfile,
} from './api';

const ROLE_LABEL: Record<UserRole, string> = { owner: 'Dueño(a)', manager: 'Gerente', staff: 'Profesor/Staff' };
const ROLE_CLS: Record<UserRole, string> = {
  owner:   'bg-accent/15 text-accent-ink',
  manager: 'bg-brand-blue/10 text-brand-blue',
  staff:   'bg-surface-2 text-content-2',
};
const LEVEL_LABEL: Record<PermLevel, string> = {
  none: 'Sin acceso', view: 'Solo ver', operate: 'Operar', manage: 'Administrar',
};

export default function UsersPage() {
  const toast = useToast();
  const { user } = useAuth();
  const isOwner = user?.baseRole === 'owner';
  const selfId = Number(user?.account_id);
  // Pinta al instante lo último conocido (caché local) y refresca por detrás.
  const [ops, setOps] = useState<Operator[] | null>(() => readCache<Operator[]>('users-list') ?? null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  const [permsFor, setPermsFor] = useState<Operator | null>(null);
  // Puestos fijos del negocio (Cajera, Marketing…). Vacío = negocio sin perfiles: todo como antes.
  const [profiles, setProfiles] = useState<StaffProfile[]>([]);

  const load = () => listOperators().then((r) => { setOps(r.users); writeCache('users-list', r.users); })
    .catch((e) => toast.error(e.message));
  useEffect(() => {
    load();
    listProfiles().then((r) => setProfiles(r.profiles)).catch(() => setProfiles([]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const conPerfiles = profiles.length > 0;
  const perfilLabel = (key?: string | null) => profiles.find((p) => p.key === key)?.label;

  async function withBusy(id: number, fn: () => Promise<unknown>, okMsg?: string) {
    setBusyId(id);
    try { await fn(); if (okMsg) toast.success(okMsg); await load(); }
    catch (e) { toast.error((e as Error).message); }
    finally { setBusyId(null); }
  }

  const toggleActive = (o: Operator) => {
    if (o.id === selfId) { toast.error('No puedes desactivar tu propia cuenta.'); return; }
    withBusy(o.id, () => patchOperator(o.id, { is_active: o.is_active ? 0 : 1 }),
      o.is_active ? 'Cuenta desactivada' : 'Cuenta activada');
  };
  const changeRole = (o: Operator, role: UserRole) =>
    withBusy(o.id, () => patchOperator(o.id, { role }), 'Rol actualizado');

  return (
    // Ruta densa (1600px) — mismo patrón de lista que Profesores: UNA tarjeta con filas anchas.
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-display text-2xl font-bold text-content">
            <Users size={24} className="text-brand-blue" /> Usuarios y roles
          </h1>
          <p className="mt-0.5 text-sm text-content-3">Tu equipo con acceso. Invita, define roles y ajusta permisos por persona.</p>
        </div>
        <button onClick={() => setShowInvite(true)}
          className="inline-flex items-center gap-2 rounded-xl bg-accent px-3.5 py-2 text-sm font-semibold text-on-accent shadow-sm transition hover:opacity-90">
          <UserPlus size={16} /> Invitar usuario
        </button>
      </div>

      {ops === null ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-accent-ink" /></div>
      ) : ops.length === 0 ? (
        <p className="py-16 text-center text-content-3">Aún no hay usuarios.</p>
      ) : (
        <div className="overflow-hidden rounded-2xl bg-surface shadow-sm">
          <div className="flex items-center gap-2 border-b border-line px-4 py-2.5 text-sm font-semibold text-content">
            <Users size={16} className="text-accent-ink" /> {ops.length} {ops.length === 1 ? 'usuario' : 'usuarios'}
          </div>
          <div className="divide-y divide-line">
          {ops.map((o) => {
            const pending = o.is_active === 1 && o.has_password === 0;
            const isSelf = o.id === selfId;
            const canTouch = isOwner || o.role !== 'owner'; // solo dueño gestiona dueños
            return (
              <div key={o.id} className="flex flex-wrap items-center gap-2.5 px-4 py-2.5">
                <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-accent/15 text-sm font-bold text-accent-ink">
                  {o.name?.[0]?.toUpperCase() || '?'}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-semibold text-content">{o.name}</span>
                    {perfilLabel(o.profile) ? (
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${ROLE_CLS.manager}`}>{perfilLabel(o.profile)}</span>
                    ) : (
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${ROLE_CLS[o.role]}`}>{ROLE_LABEL[o.role]}</span>
                    )}
                    {/* Con puestos, un gerente que no es ninguno salta a la vista: ve el dinero o está ajustado a mano. */}
                    {o.profile === 'full' && (
                      <span className="shrink-0 rounded-full bg-amber-500/10 px-2 py-0.5 text-[11px] font-semibold text-amber-500"
                        title="Gerente sin ajustes: ve finanzas, nómina y reportes">acceso completo</span>
                    )}
                    {o.profile === 'custom' && (
                      <span className="shrink-0 rounded-full bg-surface-2 px-2 py-0.5 text-[11px] font-semibold text-content-2"
                        title="Permisos ajustados a mano; no coincide con ningún puesto">personalizado</span>
                    )}
                    {isSelf && <span className="shrink-0 text-[11px] text-content-3">(tú)</span>}
                  </div>
                  <div className="truncate text-xs text-content-3">{o.email || 'sin correo'}</div>
                </div>
                <div className="shrink-0">
                  {o.is_active === 0 ? (
                    <span className="rounded-full bg-surface-2 px-2.5 py-1 text-xs font-medium text-content-3">Inactivo</span>
                  ) : pending ? (
                    <span className="rounded-full bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-500">Pendiente</span>
                  ) : (
                    <span className="rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-500">Activo</span>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  {/* Ficha de la persona (Mabel 3-oct-2026): la misma de Profesores, también para el staff.
                      Las cuentas de servicio (@lealify.com) no tienen ficha. */}
                  {!/@lealify\.com$/i.test(o.email || '') && (
                    <Link to={`/profesores/${o.id}`} title="Ver su ficha" data-testid={`perfil-${o.id}`}
                      className="rounded-lg border border-line p-1.5 text-content-2 transition hover:bg-surface-2">
                      <UserRound size={15} />
                    </Link>
                  )}
                  <select value={o.role} disabled={!canTouch || isSelf || busyId === o.id}
                    onChange={(e) => changeRole(o, e.target.value as UserRole)} title="Cambiar rol"
                    className="rounded-lg border border-line bg-surface-2 px-2 py-1 text-xs text-content-2 disabled:opacity-50">
                    <option value="owner" disabled={!isOwner}>Dueño(a)</option>
                    {/* Con puestos, subir a gerente = darle el dinero: solo el dueño (también en backend). */}
                    <option value="manager" disabled={conPerfiles && !isOwner && o.role !== 'manager'}>Gerente</option>
                    <option value="staff">Profesor/Staff</option>
                  </select>
                  {o.email && canTouch && (
                    pending ? (
                      <button onClick={() => withBusy(o.id, () => resendInvite(o.id), 'Invitación reenviada')}
                        disabled={busyId === o.id} title="Reenviar invitación"
                        className="rounded-lg border border-line p-1.5 text-content-2 transition hover:bg-surface-2 disabled:opacity-50">
                        <Mail size={15} />
                      </button>
                    ) : (
                      <button onClick={() => withBusy(o.id, () => sendReset(o.id), 'Correo de reset enviado')}
                        disabled={busyId === o.id} title="Enviar restablecer contraseña"
                        className="rounded-lg border border-line p-1.5 text-content-2 transition hover:bg-surface-2 disabled:opacity-50">
                        <KeyRound size={15} />
                      </button>
                    )
                  )}
                  {isOwner && canTouch && (
                    <button onClick={() => setPermsFor(o)} disabled={busyId === o.id} title="Permisos por módulo"
                      className="rounded-lg border border-line p-1.5 text-content-2 transition hover:bg-surface-2 disabled:opacity-50">
                      <SlidersHorizontal size={15} />
                    </button>
                  )}
                  {/* Ver como: abre la app con los ojos de esta persona (solo owner, nunca sobre owners) */}
                  {isOwner && o.role !== 'owner' && !isSelf && o.is_active === 1 && (
                    <button
                      onClick={() => withBusy(o.id, async () => { await impersonate(o.id); window.location.href = '/'; })}
                      disabled={busyId === o.id} title={`Ver la app como ${o.name}`}
                      className="rounded-lg border border-line p-1.5 text-content-2 transition hover:bg-surface-2 disabled:opacity-50">
                      <Eye size={15} />
                    </button>
                  )}
                  {canTouch && !isSelf && (
                    <button onClick={() => toggleActive(o)} disabled={busyId === o.id}
                      title={o.is_active ? 'Desactivar' : 'Activar'}
                      className={`rounded-lg p-1.5 disabled:opacity-50 ${o.is_active ? 'text-rose-500 hover:bg-rose-500/10' : 'text-emerald-500 hover:bg-emerald-500/10'}`}>
                      {o.is_active ? <X size={15} /> : <Check size={15} />}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          </div>
        </div>
      )}

      <p className="mt-5 flex items-center gap-1.5 text-xs text-content-3">
        <Shield size={13} /> "Pendiente" = invitado que aún no fija su contraseña. Solo un Dueño gestiona Dueños y permisos.
      </p>

      {showInvite && <InviteModal profiles={profiles} isOwner={isOwner}
        onClose={() => setShowInvite(false)} onDone={() => { setShowInvite(false); load(); }} />}
      {permsFor && <PermissionsModal op={permsFor} selfId={selfId} profiles={profiles}
        onClose={() => setPermsFor(null)} onChanged={() => { setPermsFor(null); load(); }} />}
    </div>
  );
}

// ── Modal de invitación (portal: el transform del layout rompe fixed) ──
// Con puestos, el selector es «Puesto»: valor 'staff' | 'p:<clave>' | 'manager' | 'owner'.
// El servidor aplica los permisos del puesto en el mismo alta: no hay segundo paso que olvidar.
function InviteModal({ profiles, isOwner, onClose, onDone }: {
  profiles: StaffProfile[]; isOwner: boolean; onClose: () => void; onDone: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [puesto, setPuesto] = useState('staff');
  const [saving, setSaving] = useState(false);
  const conPerfiles = profiles.length > 0;
  const perfil = puesto.startsWith('p:') ? profiles.find((p) => `p:${p.key}` === puesto) : undefined;
  const resumen = perfil ? perfil.summary
    : puesto === 'manager' ? 'Ve todo el estudio, incluidas finanzas, nómina y reportes.'
    : puesto === 'owner' ? 'Control total, incluidos usuarios y permisos.'
    : 'Sus clases, asistencia y alumnas. Sin caja ni dinero.';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) { toast.error('Nombre y correo son obligatorios.'); return; }
    setSaving(true);
    try {
      const role: UserRole = perfil ? perfil.role : (puesto as UserRole);
      const r = await inviteOperator({ name: name.trim(), email: email.trim(), role, ...(perfil ? { profile: perfil.key } : {}) });
      toast.success(r.invited ? 'Invitación enviada por correo.' : 'Usuario creado (sin correo de invitación).');
      onDone();
    } catch (err) { toast.error((err as Error).message); }
    finally { setSaving(false); }
  };

  const input = 'w-full rounded-lg border border-line bg-surface-2 px-3 py-2.5 text-sm text-content outline-none focus:border-accent/50';
  return createPortal(
    <div className="modal-overlay fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="modal-panel w-full rounded-t-2xl border border-line bg-surface p-5 shadow-2xl sm:max-w-md sm:rounded-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-bold text-content"><UserPlus size={18} className="text-accent-ink" /> Invitar usuario</h2>
          <button type="button" onClick={onClose} className="rounded-full p-1.5 text-content-3 hover:bg-surface-2"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-content-2">Nombre</label>
            <input className={input} value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre y apellido" autoFocus />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-content-2">Correo</label>
            <input className={input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="correo@ejemplo.com" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-content-2">{conPerfiles ? 'Puesto' : 'Rol'}</label>
            <select className={input} value={puesto} onChange={(e) => setPuesto(e.target.value)}>
              <option value="staff">Profesor/Staff</option>
              {profiles.map((p) => <option key={p.key} value={`p:${p.key}`}>{p.label}</option>)}
              {(!conPerfiles || isOwner) && <option value="manager">{conPerfiles ? 'Gerente (acceso completo)' : 'Gerente'}</option>}
              {isOwner && <option value="owner">Dueño(a)</option>}
            </select>
            {conPerfiles && <p className="mt-1.5 text-xs text-content-3">{resumen}</p>}
          </div>
        </div>
        <p className="mt-3 text-xs text-content-3">Se le enviará un correo para que fije su contraseña y active su cuenta.</p>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-line px-4 py-2 text-sm text-content-2 hover:bg-surface-2">Cancelar</button>
          <button type="submit" disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-on-accent hover:opacity-90 disabled:opacity-60">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />} Enviar invitación
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}

// ── Modal de permisos por módulo (portal) ──
function PermissionsModal({ op, selfId, profiles, onClose, onChanged }: {
  op: Operator; selfId: number; profiles: StaffProfile[]; onClose: () => void; onChanged: () => void;
}) {
  const toast = useToast();
  const [mods, setMods] = useState<ModulePermission[] | null>(null);
  const [dirty, setDirty] = useState<Record<string, PermLevel | null>>({});
  const [saving, setSaving] = useState(false);
  const puedePuesto = profiles.length > 0 && op.role !== 'owner' && op.id !== selfId;

  // Reaplicar un puesto deja rol + permisos EXACTOS del puesto (borra los ajustes a mano).
  const aplicarPuesto = async (p: StaffProfile) => {
    if (!window.confirm(`¿Dejar a ${op.name} como ${p.label}?\n\n${p.summary}\n\nSe borran sus ajustes a mano.`)) return;
    setSaving(true);
    try { await applyProfile(op.id, p.key); toast.success(`${op.name} ahora es ${p.label}`); onChanged(); }
    catch (e) { toast.error((e as Error).message); setSaving(false); }
  };

  useEffect(() => {
    getPermissions(op.id).then((r) => setMods(r.modules)).catch((e) => { toast.error(e.message); onClose(); });
  }, [op.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentOverride = (m: ModulePermission): PermLevel | null =>
    m.key in dirty ? dirty[m.key] : m.override;

  const setLevel = (m: ModulePermission, v: string) =>
    setDirty((prev) => ({ ...prev, [m.key]: v === 'inherit' ? null : (v as PermLevel) }));

  const save = async () => {
    if (!Object.keys(dirty).length) { onClose(); return; }
    setSaving(true);
    try {
      await savePermissions(op.id, dirty);
      toast.success('Permisos actualizados');
      onClose();
    } catch (e) { toast.error((e as Error).message); setSaving(false); }
  };

  return createPortal(
    <div className="modal-overlay fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="modal-panel flex max-h-[85vh] w-full flex-col rounded-t-2xl border border-line bg-surface p-5 shadow-2xl sm:max-w-md sm:rounded-2xl">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-bold text-content">
            <SlidersHorizontal size={18} className="text-accent-ink" /> Permisos de {op.name}
          </h2>
          <button type="button" onClick={onClose} className="rounded-full p-1.5 text-content-3 hover:bg-surface-2"><X size={18} /></button>
        </div>
        <p className="mb-3 text-xs text-content-3">
          Rol: <b>{ROLE_LABEL[op.role]}</b>. "Heredado" sigue lo que da su rol; cambiarlo crea un ajuste solo para esta persona.
        </p>
        {puedePuesto && (
          <div className="mb-3 flex flex-wrap items-center gap-1.5 rounded-lg bg-surface-2 px-2.5 py-2">
            <span className="text-xs text-content-2">Aplicar puesto:</span>
            {profiles.map((p) => (
              <button key={p.key} type="button" onClick={() => aplicarPuesto(p)} disabled={saving}
                title={p.summary}
                className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold transition disabled:opacity-50 ${op.profile === p.key
                  ? 'border-brand-blue/40 bg-brand-blue/10 text-brand-blue' : 'border-line text-content-2 hover:bg-surface'}`}>
                {op.profile === p.key && <Check size={11} className="mr-0.5 inline" />}{p.label}
              </button>
            ))}
          </div>
        )}

        {mods === null ? (
          <div className="flex justify-center py-10"><Loader2 className="animate-spin text-accent-ink" /></div>
        ) : (
          <div className="-mr-2 flex-1 space-y-1 overflow-y-auto pr-2">
            {mods.map((m) => {
              const ov = currentOverride(m);
              const lockSelf = m.key === 'users_admin' && op.id === selfId; // anti-lockout (también en backend)
              return (
                <div key={m.key} className={`flex items-center gap-2 rounded-lg px-2 py-1.5 ${ov !== null ? 'bg-accent/10' : ''}`}>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-content">
                      {m.name}
                      {ov !== null && <span className="ml-1.5 rounded-full bg-accent/15 px-1.5 py-0.5 text-[10px] font-semibold text-accent-ink">ajustado</span>}
                    </p>
                    <p className="text-[11px] text-content-3">Por rol: {LEVEL_LABEL[m.inherited]}</p>
                  </div>
                  <select value={ov === null ? 'inherit' : ov} disabled={lockSelf}
                    onChange={(e) => setLevel(m, e.target.value)}
                    className="shrink-0 rounded-lg border border-line bg-surface-2 px-2 py-1.5 text-xs text-content-2 disabled:opacity-50">
                    <option value="inherit">Heredado ({LEVEL_LABEL[m.inherited]})</option>
                    <option value="none">Sin acceso</option>
                    <option value="view">Solo ver</option>
                    <option value="operate">Operar</option>
                    <option value="manage">Administrar</option>
                  </select>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-4 flex items-center justify-between gap-2">
          <p className="text-[11px] text-content-3">Los cambios aplican de inmediato; la persona verá su menú actualizado al recargar.</p>
          <div className="flex shrink-0 gap-2">
            <button type="button" onClick={onClose} className="rounded-lg border border-line px-3 py-2 text-sm text-content-2 hover:bg-surface-2">Cancelar</button>
            <button type="button" onClick={save} disabled={saving || mods === null}
              className="inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-on-accent hover:opacity-90 disabled:opacity-60">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Guardar
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
